import React, { useState, useEffect } from 'react';
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  doc,
  updateDoc,
  serverTimestamp
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../config/firebase';
import { useTheme } from '../contexts/ThemeContext';
import { RefreshCw, Clock, CheckCircle, XCircle, AlertTriangle, Search, Eye, RefreshCcw, Banknote, X } from 'lucide-react';
import toast from 'react-hot-toast';

const Refunds = () => {
  const { colors } = useTheme();
  const [refunds, setRefunds] = useState([]);
  const [filteredRefunds, setFilteredRefunds] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRefund, setSelectedRefund] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [filter, setFilter] = useState('all');
  const [rejectionReason, setRejectionReason] = useState('');
  const [approvedAmount, setApprovedAmount] = useState('');
  const [manualRefundReference, setManualRefundReference] = useState('');
  const [manualRefundNote, setManualRefundNote] = useState('');
  const [closureReason, setClosureReason] = useState('');

  useEffect(() => {
    const refundsQuery = query(collection(db, 'refunds'), orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(refundsQuery, (snapshot) => {
      const refundsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
        processedAt: doc.data().processedAt?.toDate(),
      }));
      setRefunds(refundsData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const statusFilteredRefunds = filter === 'all'
      ? refunds
      : refunds.filter((refund) => refund.status === filter);

    if (searchTerm) {
      const lowerTerm = searchTerm.toLowerCase();
      setFilteredRefunds(statusFilteredRefunds.filter(r =>
        (r.bookingId || '').toLowerCase().includes(lowerTerm) ||
        (r.requestedBy || '').toLowerCase().includes(lowerTerm) ||
        (r.id || '').toLowerCase().includes(lowerTerm)
      ));
    } else {
      setFilteredRefunds(statusFilteredRefunds);
    }
  }, [searchTerm, refunds, filter]);

  useEffect(() => {
    if (!selectedRefund) return;
    const latestRefund = refunds.find((refund) => refund.id === selectedRefund.id);
    if (latestRefund) setSelectedRefund(latestRefund);
  }, [refunds, selectedRefund]);

  const handleApproveRefund = async (refundId, amount = null) => {
    setProcessing(true);
    try {
      const updateData = {
        status: 'approved',
        processedBy: 'admin',
        updatedAt: serverTimestamp(),
      };

      if (amount !== null && amount > 0) {
        updateData.approvedAmount = amount;
      }

      await updateDoc(doc(db, 'refunds', refundId), updateData);

      setSelectedRefund(null);
      toast.success('Refund successfully approved!');
    } catch (error) {
      console.error('Error approving refund:', error);
      toast.error('Failed to approve refund.');
    } finally {
      setProcessing(false);
    }
  };

  const handleRejectRefund = async (refundId, reason) => {
    setProcessing(true);
    try {
      await updateDoc(doc(db, 'refunds', refundId), {
        status: 'rejected',
        processedBy: 'admin',
        rejectionReason: reason,
        updatedAt: serverTimestamp(),
        processedAt: serverTimestamp(),
      });

      setSelectedRefund(null);
      toast.success('Refund request rejected.');
    } catch (error) {
      console.error('Error rejecting refund:', error);
      toast.error('Failed to reject refund.');
    } finally {
      setProcessing(false);
    }
  };

  const handleProcessRefund = async (refundId) => {
    setProcessing(true);
    try {
      const processApprovedRefund = httpsCallable(functions, 'processApprovedRefund');
      await processApprovedRefund({ refundId });

      setSelectedRefund(null);
      toast.success('Refund completed through Moyasar!');
    } catch (error) {
      console.error('Error processing refund:', error);
      toast.error(`Failed to process refund: ${error.message}`);
    } finally {
      setProcessing(false);
    }
  };

  const handleRetryFailedRefund = async (refundId) => {
    setProcessing(true);
    try {
      const retryFailedRefund = httpsCallable(functions, 'retryFailedRefund');
      await retryFailedRefund({ refundId });

      setSelectedRefund(null);
      toast.success('Refund retry completed through Moyasar!');
    } catch (error) {
      console.error('Error retrying failed refund:', error);
      toast.error(`Failed to retry refund: ${error.message}`);
    } finally {
      setProcessing(false);
    }
  };

  const handleMarkManualRefundCompleted = async (refundId) => {
    if (!manualRefundReference.trim()) {
      toast.error('Manual refund reference is required.');
      return;
    }

    setProcessing(true);
    try {
      const markManualRefundCompleted = httpsCallable(functions, 'markManualRefundCompleted');
      await markManualRefundCompleted({
        refundId,
        reference: manualRefundReference.trim(),
        note: manualRefundNote.trim(),
      });

      setSelectedRefund(null);
      toast.success('Manual refund marked as completed.');
    } catch (error) {
      console.error('Error marking manual refund completed:', error);
      toast.error(`Failed to mark manual refund: ${error.message}`);
    } finally {
      setProcessing(false);
    }
  };

  const handleCloseFailedRefund = async (refundId) => {
    if (!closureReason.trim()) {
      toast.error('Closure reason is required.');
      return;
    }

    setProcessing(true);
    try {
      const closeFailedRefund = httpsCallable(functions, 'closeFailedRefund');
      await closeFailedRefund({
        refundId,
        reason: closureReason.trim(),
      });

      setSelectedRefund(null);
      toast.success('Failed refund closed.');
    } catch (error) {
      console.error('Error closing failed refund:', error);
      toast.error(`Failed to close refund: ${error.message}`);
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: colors.warningGhost, color: colors.warning, text: 'Pending', icon: Clock },
      approved: { bg: colors.successGhost, color: colors.success, text: 'Approved', icon: CheckCircle },
      processing: { bg: colors.primaryGhost, color: colors.primary, text: 'Processing', icon: RefreshCw },
      completed: { bg: colors.surfaceHover, color: colors.textSecondary, text: 'Completed', icon: CheckCircle },
      failed: { bg: colors.errorGhost, color: colors.error, text: 'Failed', icon: XCircle },
      rejected: { bg: colors.errorGhost, color: colors.error, text: 'Rejected', icon: XCircle }
    };

    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;

    return (
      <span
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider"
        style={{ backgroundColor: badge.bg, color: badge.color, border: `1px solid ${badge.color}30` }}
      >
        <Icon className="w-3.5 h-3.5" />
        {badge.text}
      </span>
    );
  };

  const getReasonBadge = (reason) => {
    const reasonLabels = {
      cancellation: 'Cancellation',
      damageClaim: 'Damage Claim',
      noShow: 'No Show',
      hostCancellation: 'Host Cancellation',
      systemError: 'System Error',
      dispute: 'Dispute Resolution',
      other: 'Other'
    };

    return (
      <span className="px-2.5 py-1 rounded-md text-[11px] font-bold tracking-wider uppercase shadow-sm" style={{ backgroundColor: colors.backgroundSecondary, color: colors.textSecondary, border: `1px solid ${colors.border}` }}>
        {reasonLabels[reason] || reason || 'Standard'}
      </span>
    );
  };

  const formatCurrencyLocal = (amount) => {
    return new Intl.NumberFormat('en-SA', {
      style: 'currency',
      currency: 'SAR'
    }).format(amount || 0);
  };

  const formatDateTimeLocal = (date) => {
    if (!date) return 'N/A';
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const getStatusCounts = () => {
    return {
      all: refunds.length,
      pending: refunds.filter(r => r.status === 'pending').length,
      approved: refunds.filter(r => r.status === 'approved').length,
      processing: refunds.filter(r => r.status === 'processing').length,
      completed: refunds.filter(r => r.status === 'completed').length,
      failed: refunds.filter(r => r.status === 'failed').length,
      rejected: refunds.filter(r => r.status === 'rejected').length,
    };
  };

  const statusCounts = getStatusCounts();

  const kpis = [
    { id: 'pending', label: 'Pending Review', value: statusCounts.pending, icon: Clock, color: colors.warning, gradient: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)' },
    { id: 'approved', label: 'Awaiting Processing', value: statusCounts.approved, icon: CheckCircle, color: colors.success, gradient: 'linear-gradient(135deg, #10B981 0%, #059669 100%)' },
    { id: 'processing', label: 'In Progress', value: statusCounts.processing, icon: RefreshCw, color: colors.primary, gradient: 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)' },
    { id: 'completed', label: 'Completed', value: statusCounts.completed, icon: Banknote, color: colors.textSecondary, gradient: 'linear-gradient(135deg, #64748B 0%, #475569 100%)' },
  ];

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ color: colors.text }}>
            Refund Management
          </h1>
          <p className="mt-1 text-sm" style={{ color: colors.textSecondary }}>
            Review, approve, and process guest & host refund requests.
          </p>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((kpi, i) => {
          const isActive = filter === kpi.id;
          return (
            <div
              key={kpi.id}
              className={`card-premium p-4 cursor-pointer transition-all ${isActive ? 'ring-2' : ''} opacity-0 animate-fadeInScale stagger-${i + 1}`}
              style={{
                backgroundColor: isActive ? `${kpi.color}10` : colors.card,
                border: `1px solid ${isActive ? kpi.color : colors.border}`,
                boxShadow: isActive ? `0 4px 20px ${kpi.color}20` : undefined,
                ringColor: isActive ? kpi.color : 'transparent'
              }}
              onClick={() => setFilter(kpi.id)}
            >
              <div className="flex flex-col h-full justify-between gap-3">
                <div className="flex items-center justify-between">
                  <div
                    className="w-9 h-9 rounded-xl flex items-center justify-center shadow-sm"
                    style={{ background: kpi.gradient }}
                  >
                    <kpi.icon className="w-[18px] h-[18px] text-white" />
                  </div>
                  <p className="text-2xl font-bold" style={{ color: colors.text }}>{kpi.value}</p>
                </div>
                <p className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: isActive ? kpi.color : colors.textSecondary }}>{kpi.label}</p>
              </div>
            </div>
          )
        })}
      </div>

      {/* Filter Tabs & Search */}
      <div className="card-premium p-4" style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>
        <div className="flex flex-col xl:flex-row gap-4 items-center justify-between">
          <div className="flex flex-wrap gap-2 w-full xl:w-auto">
            {[
              { key: 'all', label: 'All' },
              { key: 'pending', label: 'Pending' },
              { key: 'approved', label: 'Approved' },
              { key: 'processing', label: 'Processing' },
              { key: 'completed', label: 'Completed' },
              { key: 'rejected', label: 'Rejected' },
              { key: 'failed', label: 'Failed' },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setFilter(tab.key)}
                className={`px-3 py-2 rounded-lg text-[12px] font-bold uppercase tracking-wider transition-all ${filter === tab.key ? 'shadow-sm' : 'hover:bg-opacity-80'}`}
                style={{
                  backgroundColor: filter === tab.key ? colors.primary : colors.backgroundSecondary,
                  color: filter === tab.key ? 'white' : colors.textSecondary,
                  border: `1px solid ${filter === tab.key ? colors.primary : colors.border}`,
                }}
              >
                {tab.label} ({statusCounts[tab.key]})
              </button>
            ))}
          </div>

          <div className="relative w-full xl:w-72 flex-shrink-0">
            <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-[16px] h-[16px]" style={{ color: colors.textMuted }} />
            <input
              type="text"
              placeholder="Search Booking ID or Reference..."
              className="w-full pl-10 pr-4 py-2 rounded-lg text-[13px] focus:outline-none transition-all duration-200"
              style={{
                backgroundColor: colors.backgroundSecondary,
                border: `1px solid ${colors.border}`,
                color: colors.text
              }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={(e) => { e.target.style.borderColor = colors.primary; e.target.style.boxShadow = `0 0 0 3px ${colors.primaryGhost}`; }}
              onBlur={(e) => { e.target.style.borderColor = colors.border; e.target.style.boxShadow = 'none'; }}
            />
          </div>
        </div>
      </div>

      {/* Refunds List */}
      <div className="card-premium overflow-hidden animate-fadeInScale" style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}>
        {loading ? (
          <div className="flex flex-col items-center justify-center p-16">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2" style={{ borderColor: colors.primary }}></div>
            <p className="mt-4 text-[14px] font-medium" style={{ color: colors.textSecondary }}>Loading requests...</p>
          </div>
        ) : filteredRefunds.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-16">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-5" style={{ backgroundColor: colors.backgroundSecondary }}>
              <RefreshCcw className="w-8 h-8" style={{ color: colors.textMuted }} />
            </div>
            <h3 className="text-[15px] font-bold" style={{ color: colors.text }}>No refunds found</h3>
            <p className="mt-1 text-[13px]" style={{ color: colors.textSecondary }}>There are no refunds matching the current criteria.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr style={{ backgroundColor: colors.surfaceHover, borderBottom: `1px solid ${colors.border}` }}>
                  <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-wider" style={{ color: colors.textMuted }}>Reference Info</th>
                  <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-wider" style={{ color: colors.textMuted }}>Refund Amount</th>
                  <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-wider" style={{ color: colors.textMuted }}>Details</th>
                  <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-wider" style={{ color: colors.textMuted }}>Status</th>
                  <th className="px-6 py-4 text-[11px] font-semibold uppercase tracking-wider text-right" style={{ color: colors.textMuted }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredRefunds.map((refund, index) => (
                  <tr
                    key={refund.id}
                    className="group transition-colors cursor-pointer hover:bg-opacity-80"
                    style={{ borderBottom: index < filteredRefunds.length - 1 ? `1px solid ${colors.border}` : 'none', backgroundColor: colors.surface }}
                    onClick={() => {
                      setSelectedRefund(refund);
                      setApprovedAmount(refund.amount || '');
                      setRejectionReason('');
                      setManualRefundReference('');
                      setManualRefundNote('');
                      setClosureReason('');
                    }}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-[13px] font-bold" style={{ color: colors.text }}>Req: {refund.id?.slice(0, 8)}...</div>
                      <div className="text-[11px] font-bold uppercase tracking-wider opacity-70 mt-1" style={{ color: colors.textSecondary }}>
                        Bkg: {refund.bookingId?.slice(0, 8) || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-[16px] font-bold" style={{ color: ['completed', 'approved'].includes(refund.status) ? colors.primary : colors.primary }}>
                        {formatCurrencyLocal(refund.approvedAmount || refund.amount)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="mb-1.5">{getReasonBadge(refund.reason)}</div>
                      <div className="text-[11px] font-bold tracking-wider uppercase" style={{ color: colors.textSecondary }}>By: {refund.requestedBy || 'Unknown'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(refund.status)}
                      <div className="text-[10px] font-bold tracking-wider mt-1.5 opacity-60 uppercase" style={{ color: colors.textSecondary }}>
                        {formatDateTimeLocal(refund.createdAt)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <button
                        className="px-3 py-2 rounded-xl text-[12px] font-bold transition-all shadow-sm hover:scale-105 inline-flex items-center gap-1.5"
                        style={{ backgroundColor: colors.primaryGhost, color: colors.primary }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedRefund(refund);
                          setApprovedAmount(refund.amount || '');
                          setRejectionReason('');
                          setManualRefundReference('');
                          setManualRefundNote('');
                          setClosureReason('');
                        }}
                      >
                        <Eye className="w-3.5 h-3.5" /> Inspect
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Details Modal */}
      {selectedRefund && (
        <div className="admin-modal-overlay fixed inset-0 z-[70] p-4 overflow-y-auto animate-fadeIn" style={{ backgroundColor: 'rgba(11, 17, 32, 0.75)', backdropFilter: 'blur(8px)' }}>
          <div className="min-h-full flex items-center justify-center">
            <div className="admin-modal-card admin-modal-scroll rounded-3xl max-w-2xl w-full p-5 sm:p-8 shadow-2xl relative animate-fadeInScale" style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}>

            <div className="absolute -top-32 -right-32 w-64 h-64 rounded-full blur-[80px]" style={{ backgroundColor: `${colors.primary}20`, pointerEvents: 'none' }}></div>

            <div className="relative z-10">
              <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-start mb-6">
                <div className="flex items-center gap-4 min-w-0">
                  <div className="w-14 h-14 rounded-2xl flex flex-col justify-center items-center shadow-lg bg-gradient-to-br from-indigo-500 to-blue-600 text-white border border-white/20">
                    <RefreshCcw className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-extrabold tracking-tight" style={{ color: colors.text }}>Refund Authorization</h3>
                    <p className="text-[13px] font-bold font-mono tracking-wider opacity-60 mt-1" style={{ color: colors.textSecondary }}>REF: {selectedRefund.id}</p>
                  </div>
                </div>
                <button onClick={() => setSelectedRefund(null)} className="p-2.5 rounded-full hover:rotate-90 transition-all duration-300 bg-white/5 shadow-sm" style={{ backgroundColor: colors.backgroundSecondary }}>
                  <X className="w-5 h-5" style={{ color: colors.text }} />
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                <div className="p-4 rounded-xl border flex flex-col justify-center" style={{ borderColor: colors.border, backgroundColor: colors.background }}>
                  <p className="text-[11px] font-extrabold uppercase tracking-widest mb-1.5" style={{ color: colors.textMuted }}>Requested Refund</p>
                  <p className="text-2xl font-black" style={{ color: colors.primary }}>{formatCurrencyLocal(selectedRefund.amount)}</p>
                </div>
                <div className="p-4 rounded-xl border flex flex-col justify-center" style={{ borderColor: colors.border, backgroundColor: colors.background }}>
                  <p className="text-[11px] font-extrabold uppercase tracking-widest mb-2" style={{ color: colors.textMuted }}>Current Status</p>
                  <div>{getStatusBadge(selectedRefund.status)}</div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-px mb-6 border rounded-xl overflow-hidden" style={{ borderColor: colors.border, backgroundColor: colors.border }}>
                <div className="p-4" style={{ backgroundColor: colors.surface }}>
                  <p className="text-[10px] font-extrabold uppercase tracking-widest mb-1.5" style={{ color: colors.textMuted }}>Filing Source</p>
                  <p className="text-[13px] font-bold" style={{ color: colors.text }}>{getReasonBadge(selectedRefund.reason)}</p>
                  <p className="text-[11px] font-bold mt-2 uppercase tracking-wide" style={{ color: colors.textSecondary }}>Req By: {selectedRefund.requestedBy}</p>
                </div>
                <div className="p-4" style={{ backgroundColor: colors.surface }}>
                  <p className="text-[10px] font-extrabold uppercase tracking-widest mb-1.5" style={{ color: colors.textMuted }}>Associated Entities</p>
                  <p className="text-[12px] font-bold font-mono tracking-wide" style={{ color: colors.textSecondary }}>BKG: <span style={{ color: colors.text }}>{selectedRefund.bookingId || 'N/A'}</span></p>
                  <p className="text-[12px] font-bold font-mono tracking-wide mt-1" style={{ color: colors.textSecondary }}>PAY: <span style={{ color: colors.text }}>{selectedRefund.paymentId || 'N/A'}</span></p>
                </div>
              </div>

              <div className="flex flex-wrap gap-4 mb-6 text-[11px] font-bold uppercase tracking-wider" style={{ color: colors.textSecondary }}>
                <div className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Filed: {formatDateTimeLocal(selectedRefund.createdAt)}</div>
                {selectedRefund.updatedAt && <div className="flex items-center gap-1.5"><RefreshCw className="w-3.5 h-3.5" /> Modified: {formatDateTimeLocal(selectedRefund.updatedAt)}</div>}
              </div>

              {selectedRefund.notes && (
                <div className="p-4 rounded-xl mb-6 relative overflow-hidden" style={{ backgroundColor: colors.backgroundSecondary, border: `1px solid ${colors.border}` }}>
                  <div className="absolute top-0 left-0 w-1 h-full bg-blue-500"></div>
                  <p className="text-[11px] font-bold uppercase tracking-wider mb-1" style={{ color: colors.textSecondary }}>Filer Notes</p>
                  <p className="text-[13px] leading-relaxed" style={{ color: colors.text }}>{selectedRefund.notes}</p>
                </div>
              )}

              {selectedRefund.adminNotes && (
                <div className="p-4 rounded-xl mb-6 relative overflow-hidden" style={{ backgroundColor: colors.backgroundSecondary, border: `1px solid ${colors.border}` }}>
                  <div className="absolute top-0 left-0 w-1 h-full bg-indigo-500"></div>
                  <p className="text-[11px] font-bold uppercase tracking-wider mb-1" style={{ color: colors.textSecondary }}>Evaluation Notes</p>
                  <p className="text-[13px] leading-relaxed" style={{ color: colors.text }}>{selectedRefund.adminNotes}</p>
                </div>
              )}

              {(selectedRefund.rejectionReason || selectedRefund.failureReason) && (
                <div className="p-4 rounded-xl mb-6 flex gap-3 items-start" style={{ backgroundColor: colors.errorGhost, border: `1px solid ${colors.error}30`, color: colors.error }}>
                  <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                  <div>
                    <h4 className="font-bold text-[12px] uppercase tracking-wider">Exception Log</h4>
                    <p className="mt-1 text-[13px] font-medium leading-relaxed">{selectedRefund.rejectionReason || selectedRefund.failureReason}</p>
                  </div>
                </div>
              )}

              {selectedRefund.moyasarRefundId && (
                <div className="p-4 rounded-xl mb-6 flex justify-between items-center" style={{ backgroundColor: colors.successGhost, border: `1px solid ${colors.success}30` }}>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: colors.success }}>Gateway Ref</p>
                    <p className="font-mono text-[14px] font-bold mt-0.5" style={{ color: colors.success }}>{selectedRefund.moyasarRefundId}</p>
                  </div>
                  <CheckCircle className="w-6 h-6" style={{ color: colors.success }} />
                </div>
              )}

              {/* Action Area based on status */}
              <div className="pt-4 border-t" style={{ borderColor: colors.border }}>
                {selectedRefund.status === 'pending' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <label className="block text-[11px] font-bold uppercase tracking-wider" style={{ color: colors.textSecondary }}>Valid Authorization Amount (SAR)</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={approvedAmount}
                        onChange={(e) => setApprovedAmount(e.target.value)}
                        className="w-full px-4 py-3 rounded-xl text-[14px] font-bold focus:outline-none transition-all"
                        style={{ backgroundColor: colors.background, color: colors.text, border: `1px solid ${colors.border}` }}
                        onFocus={(e) => { e.target.style.borderColor = colors.success; e.target.style.boxShadow = `0 0 0 3px ${colors.successGhost}`; }}
                        onBlur={(e) => { e.target.style.borderColor = colors.border; e.target.style.boxShadow = 'none'; }}
                      />
                      <button
                        onClick={() => {
                          const val = parseFloat(approvedAmount);
                          if (!val || val <= 0) return toast.error("Invalid amount");
                          handleApproveRefund(selectedRefund.id, val);
                        }}
                        disabled={processing}
                        className="w-full py-3.5 rounded-xl font-bold text-white transition-all shadow-md hover:-translate-y-0.5 disabled:opacity-50"
                        style={{ background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)' }}
                      >
                        Approve Authorization
                      </button>
                    </div>
                    <div className="space-y-3 border-t md:border-t-0 md:border-l pt-4 md:pt-0 md:pl-6" style={{ borderColor: colors.border }}>
                      <label className="block text-[11px] font-bold uppercase tracking-wider" style={{ color: colors.error }}>Denial Justification</label>
                      <input
                        type="text"
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                        className="w-full px-4 py-3 rounded-xl text-[14px] focus:outline-none transition-all"
                        placeholder="Enter explicit reason..."
                        style={{ backgroundColor: colors.background, color: colors.text, border: `1px solid ${colors.border}` }}
                        onFocus={(e) => { e.target.style.borderColor = colors.error; e.target.style.boxShadow = `0 0 0 3px ${colors.errorGhost}`; }}
                        onBlur={(e) => { e.target.style.borderColor = colors.border; e.target.style.boxShadow = 'none'; }}
                      />
                      <button
                        onClick={() => {
                          if (!rejectionReason.trim()) return toast.error("Reason is mandatory for denial");
                          handleRejectRefund(selectedRefund.id, rejectionReason);
                        }}
                        disabled={processing}
                        className="w-full py-3.5 rounded-xl font-bold text-white transition-all shadow-md hover:-translate-y-0.5 disabled:opacity-50"
                        style={{ background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)' }}
                      >
                        Reject Authorization
                      </button>
                    </div>
                  </div>
                )}

                {selectedRefund.status === 'approved' && (
                  <div className="text-center py-4">
                    <p className="text-[14px] mb-4 font-medium" style={{ color: colors.textSecondary }}>This refund has been approved by admin and is ready to be issued through Moyasar Gateway.</p>
                    <button
                      onClick={() => handleProcessRefund(selectedRefund.id)}
                      disabled={processing}
                      className="w-full max-w-sm py-3.5 rounded-xl font-bold text-white transition-all shadow-md hover:-translate-y-0.5 disabled:opacity-50 inline-flex items-center justify-center gap-2 mx-auto"
                      style={{ background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)' }}
                    >
                      <RefreshCw className={`w-5 h-5 ${processing ? 'animate-spin' : ''}`} /> Execute Payment Gateway Refund
                    </button>
                  </div>
                )}

                {selectedRefund.status === 'failed' && (
                  <div className="space-y-6">
                    <div className="text-center py-2">
                      <p className="text-[14px] mb-4 font-medium" style={{ color: colors.textSecondary }}>
                        The gateway attempt failed. Choose how to resolve this refund instead of leaving it stranded.
                      </p>
                      <button
                        onClick={() => handleRetryFailedRefund(selectedRefund.id)}
                        disabled={processing}
                        className="w-full max-w-sm py-3.5 rounded-xl font-bold text-white transition-all shadow-md hover:-translate-y-0.5 disabled:opacity-50 inline-flex items-center justify-center gap-2 mx-auto"
                        style={{ background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)' }}
                      >
                        <RefreshCw className={`w-5 h-5 ${processing ? 'animate-spin' : ''}`} /> Retry Gateway Refund
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 border-t pt-6" style={{ borderColor: colors.border }}>
                      <div className="space-y-3">
                        <label className="block text-[11px] font-bold uppercase tracking-wider" style={{ color: colors.success }}>
                          Manual Refund Reference
                        </label>
                        <input
                          type="text"
                          value={manualRefundReference}
                          onChange={(e) => setManualRefundReference(e.target.value)}
                          className="w-full px-4 py-3 rounded-xl text-[14px] focus:outline-none transition-all"
                          placeholder="Bank transfer / receipt / ticket ID"
                          style={{ backgroundColor: colors.background, color: colors.text, border: `1px solid ${colors.border}` }}
                        />
                        <textarea
                          value={manualRefundNote}
                          onChange={(e) => setManualRefundNote(e.target.value)}
                          className="w-full px-4 py-3 rounded-xl text-[14px] focus:outline-none transition-all min-h-[96px]"
                          placeholder="Optional note for audit trail..."
                          style={{ backgroundColor: colors.background, color: colors.text, border: `1px solid ${colors.border}` }}
                        />
                        <button
                          onClick={() => handleMarkManualRefundCompleted(selectedRefund.id)}
                          disabled={processing}
                          className="w-full py-3.5 rounded-xl font-bold text-white transition-all shadow-md hover:-translate-y-0.5 disabled:opacity-50"
                          style={{ background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)' }}
                        >
                          Mark Manual Refund Completed
                        </button>
                      </div>

                      <div className="space-y-3 border-t md:border-t-0 md:border-l pt-4 md:pt-0 md:pl-6" style={{ borderColor: colors.border }}>
                        <label className="block text-[11px] font-bold uppercase tracking-wider" style={{ color: colors.error }}>
                          Close Without Refund
                        </label>
                        <textarea
                          value={closureReason}
                          onChange={(e) => setClosureReason(e.target.value)}
                          className="w-full px-4 py-3 rounded-xl text-[14px] focus:outline-none transition-all min-h-[138px]"
                          placeholder="Explain why this failed refund is being closed..."
                          style={{ backgroundColor: colors.background, color: colors.text, border: `1px solid ${colors.border}` }}
                        />
                        <button
                          onClick={() => handleCloseFailedRefund(selectedRefund.id)}
                          disabled={processing}
                          className="w-full py-3.5 rounded-xl font-bold text-white transition-all shadow-md hover:-translate-y-0.5 disabled:opacity-50"
                          style={{ background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)' }}
                        >
                          Close Failed Refund
                        </button>
                      </div>
                    </div>
                  </div>
                )}

              </div>

            </div>
          </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Refunds;
